import type { FormValues } from "@/widgets/classic-form/form";
import { getEmptyFormValues } from "@/widgets/classic-form/form";
import type { FormMetadataResponse, OtherFieldGroup } from "@/entities/form-metadata";
import { FormLevelFeatureType, GroupType } from "@/entities/form-metadata";
import { populateTimespanFeature } from "./featureMappers";
import {
    populateEventFields,
    populateFamilyMemberFields,
    populateHierarchicalFields,
    populateNameFields,
    populateOtherFields,
    populateSpecialFields,
} from "./groupTypesMappers";

export const populateFormValuesFromMetadata = (options: {
    isEditForm: boolean;
    formMetadata: FormMetadataResponse;
    sourceFormValues: FormValues;
    matchAllTermsExactly: boolean;
    defaultYearProximity?: number | null;
}): FormValues => {
    const { isEditForm, formMetadata, sourceFormValues, matchAllTermsExactly, defaultYearProximity = null } = options;

    const formValues: FormValues = getEmptyFormValues();
    formValues.recordSubject = sourceFormValues.recordSubject;
    formValues.treePerson = sourceFormValues.treePerson;
    // priority/location are always pass-through from the URL-derived dataModel regardless of whether
    // CollectionFocus feature is present — they are serialized by the canonicalizer unconditionally.
    formValues.priority = sourceFormValues.priority;
    formValues.location = sourceFormValues.location;

    formMetadata.fieldGroupsAndFeatureContainers?.forEach((fieldGroupOrFeature) => {
        if (!("groupType" in fieldGroupOrFeature)) return;

        if (fieldGroupOrFeature.groupType === GroupType.Name) {
            populateNameFields({
                fieldGroup: fieldGroupOrFeature,
                dataModel: sourceFormValues,
                formValuesNameData: formValues.name,
                formValuesFieldsData: formValues.fields,
                matchAllTermsExactly,
                isEditForm,
            });
        } else if (fieldGroupOrFeature.groupType === GroupType.Hierarchical) {
            populateHierarchicalFields({
                fieldGroup: fieldGroupOrFeature,
                dataModel: sourceFormValues,
                formValuesEventsData: formValues.events,
                matchAllTermsExactly,
                isEditForm,
                defaultYearProximity,
            });
        } else if (fieldGroupOrFeature.groupType === GroupType.Event) {
            populateEventFields({
                fieldGroup: fieldGroupOrFeature,
                dataModel: sourceFormValues,
                formValuesEventsData: formValues.events,
                isEditForm,
                matchAllTermsExactly,
                defaultYearProximity,
            });
        } else if (fieldGroupOrFeature.groupType === GroupType.FamilyMember) {
            populateFamilyMemberFields({
                fieldGroup: fieldGroupOrFeature,
                dataModel: sourceFormValues,
                formValuesFamilyData: formValues.family,
                isEditForm,
                matchAllTermsExactly,
            });
        } else if (fieldGroupOrFeature.groupType === GroupType.Other) {
            populateOtherFields({
                fieldGroup: fieldGroupOrFeature as OtherFieldGroup,
                dataModel: sourceFormValues,
                formValuesFieldsData: formValues.fields,
                matchAllTermsExactly,
                isEditForm,
            });
        } else if (fieldGroupOrFeature.groupType === GroupType.Special) {
            populateSpecialFields({
                fieldGroup: fieldGroupOrFeature,
                dataModel: sourceFormValues,
                formValuesEventsData: formValues.events,
                isEditForm,
            });
        }
    });

    formMetadata.features.forEach((feature) => {
        if (feature.featureType === FormLevelFeatureType.CategoryBuckets) {
            formValues.types = sourceFormValues.types;
        } else if (feature.featureType === FormLevelFeatureType.ShowUnviewedRecordsOnly) {
            formValues.showUnviewedRecordsOnly = sourceFormValues.showUnviewedRecordsOnly;
        } else if (feature.featureType === FormLevelFeatureType.Timespan) {
            populateTimespanFeature({
                feature,
                dataModel: sourceFormValues,
                formValues,
                matchAllTermsExactly,
            });
        }
    });

    return formValues;
};
